import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#deactivate());
    // on load, cache the max height of the submenu so you can use it in a translate
    this.#cacheMaxOverflowMenuHeight();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.#cleanupMutationObserver();
    this.#cleanupGraceZone();
  }

  /**
   * Debounced resize event listener to recalculate menu style and alignment
   */
  #resizeListener = debounce(() => {
    this.#cacheMaxOverflowMenuHeight();
    setHeaderMenuStyle();
    this.#alignActiveSubmenu();
  }, 100);

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, {childList: true, subtree: true});

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    const submenuHeight = submenu ? submenu.offsetHeight : 0;

    this.headerComponent?.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');

    this.#alignActiveSubmenu();
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) return;

    // If submenu is visible, use a grace zone to allow diagonal mouse movement
    if (event instanceof PointerEvent && menu && menu.offsetHeight > 0) {
      this.#startGraceZone(event, menu);
      return;
    }

    this.#deactivate();
  }

  /**
   * Monitor mouse movement and keep the menu open while the cursor
   * stays within a triangular grace zone toward the submenu.
   * @param {PointerEvent} leaveEvent
   * @param {HTMLElement} submenu
   */
  #startGraceZone(leaveEvent, submenu) {
    // Clean up any previous grace zone listener
    this.#cleanupGraceZone();

    const submenuRect = submenu.getBoundingClientRect();
    const startX = leaveEvent.clientX;
    const startY = leaveEvent.clientY;

    const handler = (/** @type {PointerEvent} */ moveEvent) => {
      const {clientX, clientY} = moveEvent;

      // Cursor reached the submenu — keep it open
      if (clientY >= submenuRect.top && clientY <= submenuRect.bottom) {
        this.#cleanupGraceZone();
        return;
      }

      // Cursor moved far above the header link — close
      if (clientY < startY - 50) {
        this.#cleanupGraceZone();
        this.#deactivate();
        return;
      }

      // Expanding triangular corridor from leave point toward the full-width submenu.
      // The corridor widens as the cursor moves downward, allowing diagonal movement.
      const verticalDistance = submenuRect.top - startY;
      const progress = verticalDistance > 0 ? Math.min(1, Math.max(0, (clientY - startY) / verticalDistance)) : 1;
      const corridorLeft = startX - (startX - submenuRect.left) * progress - 200;
      const corridorRight = startX + (submenuRect.right - startX) * progress + 200;

      if (clientX < corridorLeft || clientX > corridorRight) {
        this.#cleanupGraceZone();
        this.#deactivate();
      }
    };

    this.#graceZoneHandler = handler;
    document.addEventListener('pointermove', handler);

    // Safety timeout — always clean up after 500ms
    this.#graceZoneTimeout = setTimeout(() => {
      if (this.#graceZoneHandler) {
        this.#cleanupGraceZone();
        this.#deactivate();
      }
    }, 500);
  }

  /** @type {((e: PointerEvent) => void) | null} */
  #graceZoneHandler = null;
  /** @type {ReturnType<typeof setTimeout> | null} */
  #graceZoneTimeout = null;

  #cleanupGraceZone() {
    if (this.#graceZoneHandler) {
      document.removeEventListener('pointermove', this.#graceZoneHandler);
      this.#graceZoneHandler = null;
    }
    if (this.#graceZoneTimeout) {
      clearTimeout(this.#graceZoneTimeout);
      this.#graceZoneTimeout = null;
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.#cleanupGraceZone();
    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    // Reset submenu alignment
    const megaList = submenu?.querySelector('.mega-menu__list');
    if (megaList) {
      megaList.style.transform = '';
    }

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  /**
   * Caches the maximum height of all submenus for consistent animations
   * Stores the value in a CSS custom property for use in transitions
   */
  #cacheMaxOverflowMenuHeight() {
    const submenus = this.querySelectorAll('[ref="submenu[]"]');
    const maxHeight = Math.max(
      ...Array.from(submenus)
        .filter((submenu) => submenu instanceof HTMLElement)
        .map((submenu) => submenu.offsetHeight)
    );
    this.headerComponent?.style.setProperty('--submenu-max-height', `${maxHeight}px`);
  }

  /**
   * Align the active submenu's mega list with its header link's horizontal position.
   * Called on activation and on resize to keep alignment in sync with layout changes.
   */
  #alignActiveSubmenu() {
    const item = this.#state.activeItem;
    if (!item) return;

    const submenu = findSubmenu(item);
    if (!submenu) return;

    const megaList = submenu.querySelector('.menu-list__submenu-inner')?.querySelector('.mega-menu__list');
    if (!megaList) return;

    // Reset first to measure default position
    megaList.style.transform = '';
    void megaList.offsetWidth;

    const linkTextEl = item.querySelector('.menu-list__link-title') || item;
    const linkLeft = linkTextEl.getBoundingClientRect().left;
    const listLeft = megaList.getBoundingClientRect().left;
    const offset = linkLeft - listLeft;

    if (offset !== 0) {
      megaList.style.transform = `translateX(${offset}px)`;
    }
  }

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
